import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent, useContentToken } from '@vibesites/runtime/react';
import { motion } from 'framer-motion';
import { CountUp } from './CountUp';
import { alaViewport } from './alaMotion';
import { plainText } from './propertyUtils';

const STAT_VALUE_CLASS =
  'font-heading text-[clamp(28px,2.8vw,42px)] font-bold leading-none tracking-[-0.03em] text-[rgb(var(--ala-text))]';
const STAT_LABEL_CLASS =
  'max-w-[240px] font-heading text-base font-normal leading-[1.4] text-[rgb(var(--ala-text-muted))]';
const CELL_CLASS = 'flex flex-col gap-5 px-6 py-8 md:px-8 md:py-10 lg:px-10 lg:py-8';
const DIVIDER = 'border-[rgb(var(--ala-text))]/12';

export function InvestorsStats() {
  const value1 = plainText(useContentToken('investorsstats.stat_1_value')) || '100,000+';
  const value2 = plainText(useContentToken('investorsstats.stat_2_value')) || '105+';
  const value3 = plainText(useContentToken('investorsstats.stat_3_value')) || '37,000+';
  const value4 = plainText(useContentToken('investorsstats.stat_4_value')) || '$4B+';

  return (
    <section className="w-full bg-[rgb(var(--ala-background))] px-6 py-14 md:px-[clamp(16px,6vw,87px)] md:py-20">
      <div className="mx-auto max-w-[1440px]">
        <div className={`grid grid-cols-1 border-t ${DIVIDER} md:grid-cols-2 lg:grid-cols-4`}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.55 }}
            className={`${CELL_CLASS} border-b ${DIVIDER} md:border-r lg:border-b-0`}
          >
            <div className="relative">
              <span className="sr-only">
                <RichTextContent path="investorsstats.stat_1_value" label="Stat 1 Value" as="span" />
              </span>
              <CountUp value={value1} className={STAT_VALUE_CLASS} />
            </div>
            <RichTextContent
              path="investorsstats.stat_1_label"
              label="Stat 1 Label"
              as="p"
              className={STAT_LABEL_CLASS}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.55, delay: 0.08 }}
            className={`${CELL_CLASS} border-b ${DIVIDER} lg:border-b-0 lg:border-r`}
          >
            <div className="relative">
              <span className="sr-only">
                <RichTextContent path="investorsstats.stat_2_value" label="Stat 2 Value" as="span" />
              </span>
              <CountUp value={value2} className={STAT_VALUE_CLASS} />
            </div>
            <RichTextContent
              path="investorsstats.stat_2_label"
              label="Stat 2 Label"
              as="p"
              className={STAT_LABEL_CLASS}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.55, delay: 0.16 }}
            className={`${CELL_CLASS} border-b ${DIVIDER} md:border-r md:border-b-0 lg:border-r`}
          >
            <div className="relative">
              <span className="sr-only">
                <RichTextContent path="investorsstats.stat_3_value" label="Stat 3 Value" as="span" />
              </span>
              <CountUp value={value3} className={STAT_VALUE_CLASS} />
            </div>
            <RichTextContent
              path="investorsstats.stat_3_label"
              label="Stat 3 Label"
              as="p"
              className={STAT_LABEL_CLASS}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.55, delay: 0.24 }}
            className={CELL_CLASS}
          >
            <div className="relative">
              <span className="sr-only">
                <RichTextContent path="investorsstats.stat_4_value" label="Stat 4 Value" as="span" />
              </span>
              <CountUp value={value4} className={STAT_VALUE_CLASS} />
            </div>
            <RichTextContent
              path="investorsstats.stat_4_label"
              label="Stat 4 Label"
              as="p"
              className={STAT_LABEL_CLASS}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'InvestorsStats',
  displayName: 'Investors Stats',
  tokens: {
    'investorsstats.stat_1_value': {
      label: 'Stat 1 Value',
      type: 'richtext',
      defaultValue: '<p>100,000+</p>',
    },
    'investorsstats.stat_1_label': {
      label: 'Stat 1 Label',
      type: 'richtext',
      defaultValue: '<p>Units across four portfolio builds led by our founder</p>',
    },
    'investorsstats.stat_2_value': {
      label: 'Stat 2 Value',
      type: 'richtext',
      defaultValue: '<p>105+</p>',
    },
    'investorsstats.stat_2_label': {
      label: 'Stat 2 Label',
      type: 'richtext',
      defaultValue: '<p>Acquisitions closed since 2018</p>',
    },
    'investorsstats.stat_3_value': {
      label: 'Stat 3 Value',
      type: 'richtext',
      defaultValue: '<p>37,000+</p>',
    },
    'investorsstats.stat_3_label': {
      label: 'Stat 3 Label',
      type: 'richtext',
      defaultValue: '<p>Units renovated since 2015</p>',
    },
    'investorsstats.stat_4_value': {
      label: 'Stat 4 Value',
      type: 'richtext',
      defaultValue: '<p>$4B+</p>',
    },
    'investorsstats.stat_4_label': {
      label: 'Stat 4 Label',
      type: 'richtext',
      defaultValue: '<p>Disposition volume across 20,000+ units</p>',
    },
  },
};

(InvestorsStats as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default InvestorsStats;
